/**
 * O DAG de construcao.
 *
 * GUARDE DEFINICOES, NAO COORDENADAS. Um ponto nao e "(3, 7)": e "a interseccao do
 * circulo k1 com o circulo k2, ramo 0". As coordenadas sao DERIVADAS por `evaluate()`.
 *
 * E disso que dependem as duas coisas mais importantes do projeto:
 *
 *  - a correcao aceita QUALQUER caminho de construcao valido, porque o corretor nunca
 *    olha o caminho -- so avalia um predicado sobre o resultado;
 *  - a correcao REPROVA quem acertou no olho, porque ao perturbar os dados do item e
 *    reavaliar o DAG do proprio aluno, um ponto `free` fica parado enquanto uma
 *    construcao de verdade se move junto.
 *
 * Se guardassemos coordenadas, as duas seriam impossiveis.
 */

import type { Vec2 } from './vec.ts';
import { add, dist, lerp, midpoint, normalize, scale, sub, angleOf, fromAngle } from './vec.ts';
import type { CircleGeom, LineGeom } from './primitives.ts';
import {
  intersectCircles,
  intersectLineCircle,
  intersectLines,
  lineThrough,
  projectOnLine,
} from './primitives.ts';
import { perp } from './vec.ts';

export type GeoId = string & { readonly __geo: unique symbol };
export const geoId = (s: string): GeoId => s as GeoId;

export type Span = 'segment' | 'ray' | 'line';

// ---------------------------------------------------------------------------
// Definicoes
// ---------------------------------------------------------------------------

export type PointDef =
  /** Dado do problema; a posicao vem de `givenPositions`. */
  | { readonly via: 'given' }
  /**
   * O aluno largou no vazio, a olho. NAO e derivado de nada.
   * E este `via` que a checagem de robustez usa para separar construcao de estimativa.
   */
  | { readonly via: 'free'; readonly at: Vec2 }
  /** Sobre um objeto, no parametro t (comprimento de arco para reta, angulo para circulo). */
  | { readonly via: 'on-object'; readonly host: GeoId; readonly t: number }
  /** Interseccao de dois objetos; `branch` desambigua os dois pontos possiveis. */
  | { readonly via: 'intersection'; readonly a: GeoId; readonly b: GeoId; readonly branch: 0 | 1 }
  | { readonly via: 'midpoint'; readonly a: GeoId; readonly b: GeoId }
  | { readonly via: 'reflect'; readonly p: GeoId; readonly mirror: GeoId }
  /** Ponto de coordenadas inteiras numa malha. */
  | { readonly via: 'lattice'; readonly i: number; readonly j: number; readonly step: number };

export type LineDef =
  | { readonly via: 'through'; readonly a: GeoId; readonly b: GeoId; readonly span: Span }
  | { readonly via: 'perpendicular'; readonly through: GeoId; readonly to: GeoId }
  | { readonly via: 'parallel'; readonly through: GeoId; readonly to: GeoId }
  | { readonly via: 'perp-bisector'; readonly a: GeoId; readonly b: GeoId }
  | { readonly via: 'bisector'; readonly a: GeoId; readonly vertex: GeoId; readonly b: GeoId };

export type CircleDef =
  | { readonly via: 'center-through'; readonly center: GeoId; readonly through: GeoId }
  /** O compasso de verdade: raio transportado de outro par de pontos. */
  | {
      readonly via: 'compass';
      readonly center: GeoId;
      readonly radiusA: GeoId;
      readonly radiusB: GeoId;
    };

export type AnyDef = PointDef | LineDef | CircleDef;
export type GeoKind = 'point' | 'line' | 'circle';

// ---------------------------------------------------------------------------
// Objetos avaliados
// ---------------------------------------------------------------------------

export interface GPoint {
  readonly id: GeoId;
  readonly kind: 'point';
  readonly def: PointDef;
  readonly name: string;
  readonly pos: Vec2;
}

export interface GLine {
  readonly id: GeoId;
  readonly kind: 'line';
  readonly def: LineDef;
  readonly name: string;
  /** Ponto de origem (para semirreta e segmento, e o extremo inicial). */
  readonly p: Vec2;
  /** Direcao unitaria. */
  readonly dir: Vec2;
  readonly span: Span;
  /** Segundo ponto definidor (extremo final do segmento; ponto-guia da semirreta). */
  readonly q: Vec2;
}

export interface GCircle {
  readonly id: GeoId;
  readonly kind: 'circle';
  readonly def: CircleDef;
  readonly name: string;
  readonly c: Vec2;
  readonly r: number;
}

export type GeoObject = GPoint | GLine | GCircle;

export const asLineGeom = (l: GLine): LineGeom => ({ p: l.p, dir: l.dir });
export const asCircleGeom = (k: GCircle): CircleGeom => ({ c: k.c, r: k.r });

// ---------------------------------------------------------------------------
// Snapshot: o DAG serializavel
// ---------------------------------------------------------------------------

export interface ConstructionSnapshot {
  /** Ordem topologica; coincide com a ordem de criacao. */
  readonly order: readonly GeoId[];
  readonly defs: Readonly<Record<string, AnyDef>>;
  readonly kinds: Readonly<Record<string, GeoKind>>;
  readonly names: Readonly<Record<string, string>>;
  /** Posicoes dos pontos `given`; e o que a perturbacao move. */
  readonly givenPositions: Readonly<Record<string, Vec2>>;
}

export const EMPTY_SNAPSHOT: ConstructionSnapshot = Object.freeze({
  order: [],
  defs: {},
  kinds: {},
  names: {},
  givenPositions: {},
});

export interface AddOptions {
  readonly id: GeoId;
  readonly kind: GeoKind;
  readonly def: AnyDef;
  readonly name?: string;
  readonly at?: Vec2;
}

/** Acrescenta um objeto ao DAG. Puro: devolve um snapshot novo. */
export function addObject(snap: ConstructionSnapshot, o: AddOptions): ConstructionSnapshot {
  const key = o.id as string;
  return {
    order: [...snap.order, o.id],
    defs: { ...snap.defs, [key]: o.def },
    kinds: { ...snap.kinds, [key]: o.kind },
    names: { ...snap.names, [key]: o.name ?? '' },
    givenPositions:
      o.at !== undefined ? { ...snap.givenPositions, [key]: o.at } : snap.givenPositions,
  };
}

/** Remove um objeto e tudo que depende dele (a poda tem que ser transitiva). */
export function removeObject(snap: ConstructionSnapshot, id: GeoId): ConstructionSnapshot {
  const doomed = new Set<string>([id as string]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const oid of snap.order) {
      const key = oid as string;
      if (doomed.has(key)) continue;
      const def = snap.defs[key];
      if (def && dependenciesOf(def).some((d) => doomed.has(d as string))) {
        doomed.add(key);
        changed = true;
      }
    }
  }

  const order = snap.order.filter((o) => !doomed.has(o as string));
  const keep = <T>(rec: Readonly<Record<string, T>>): Record<string, T> =>
    Object.fromEntries(Object.entries(rec).filter(([k]) => !doomed.has(k)));

  return {
    order,
    defs: keep(snap.defs),
    kinds: keep(snap.kinds),
    names: keep(snap.names),
    givenPositions: keep(snap.givenPositions),
  };
}

/** De quais objetos esta definicao depende. */
export function dependenciesOf(def: AnyDef): GeoId[] {
  switch (def.via) {
    case 'given':
    case 'free':
    case 'lattice':
      return [];
    case 'on-object':
      return [def.host];
    case 'intersection':
      return [def.a, def.b];
    case 'midpoint':
      return [def.a, def.b];
    case 'reflect':
      return [def.p, def.mirror];
    case 'through':
      return [def.a, def.b];
    case 'perpendicular':
    case 'parallel':
      return [def.through, def.to];
    case 'perp-bisector':
      return [def.a, def.b];
    case 'bisector':
      return [def.a, def.vertex, def.b];
    case 'center-through':
      return [def.center, def.through];
    case 'compass':
      return [def.center, def.radiusA, def.radiusB];
  }
}

/** Gera um id novo, estavel e legivel. */
export function freshId(snap: ConstructionSnapshot, prefix: string): GeoId {
  let n = 1;
  while (snap.defs[`${prefix}${n}`] !== undefined) n++;
  return geoId(`${prefix}${n}`);
}

// ---------------------------------------------------------------------------
// Avaliacao
// ---------------------------------------------------------------------------

export interface EvaluationResult {
  readonly objects: ReadonlyMap<GeoId, GeoObject>;
  /** Objetos que nao puderam ser construidos (retas paralelas, circulos disjuntos...). */
  readonly degenerate: readonly GeoId[];
}

/**
 * Avalia o DAG em ordem topologica.
 *
 * Um passo degenerado (interseccao inexistente, ponto medio de coincidentes) nao
 * lanca: marca-se o objeto como degenerado e tudo que dependia dele tambem cai.
 * Isso importa porque na checagem de robustez os dados sao PERTURBADOS, e uma
 * perturbacao pode legitimamente degenerar a construcao de alguem -- e isso e
 * informacao, nao crash.
 *
 * `override` permite reavaliar o mesmo DAG com outras posicoes para os `given` --
 * que e exatamente o que a perturbacao faz.
 */
export function evaluate(
  snap: ConstructionSnapshot,
  override?: Readonly<Record<string, Vec2>>,
): EvaluationResult {
  const objects = new Map<GeoId, GeoObject>();
  const degenerate: GeoId[] = [];

  const givens = override ? { ...snap.givenPositions, ...override } : snap.givenPositions;

  for (const id of snap.order) {
    const key = id as string;
    const def = snap.defs[key];
    const kind = snap.kinds[key];
    if (!def || !kind) {
      degenerate.push(id);
      continue;
    }

    // Se alguma dependencia caiu, este objeto tambem cai.
    if (dependenciesOf(def).some((d) => !objects.has(d))) {
      degenerate.push(id);
      continue;
    }

    const name = snap.names[key] ?? '';
    const built = build(kind, id, def, name, objects, givens);
    if (built === null) degenerate.push(id);
    else objects.set(id, built);
  }

  return { objects, degenerate };
}

function build(
  kind: GeoKind,
  id: GeoId,
  def: AnyDef,
  name: string,
  objects: ReadonlyMap<GeoId, GeoObject>,
  givens: Readonly<Record<string, Vec2>>,
): GeoObject | null {
  const P = (gid: GeoId): GPoint | null => {
    const o = objects.get(gid);
    return o && o.kind === 'point' ? o : null;
  };
  const L = (gid: GeoId): GLine | null => {
    const o = objects.get(gid);
    return o && o.kind === 'line' ? o : null;
  };
  const anyObj = (gid: GeoId): GeoObject | null => objects.get(gid) ?? null;

  if (kind === 'point') {
    const pos = buildPoint(def as PointDef, id, P, anyObj, givens);
    if (pos === null) return null;
    return { id, kind: 'point', def: def as PointDef, name, pos };
  }

  if (kind === 'line') {
    const d = def as LineDef;
    const res = buildLine(d, P, L);
    if (res === null) return null;
    return { id, kind: 'line', def: d, name, p: res.p, dir: res.dir, span: res.span, q: res.q };
  }

  const d = def as CircleDef;
  const center = P(d.center);
  if (!center) return null;
  let r: number;
  if (d.via === 'center-through') {
    const through = P(d.through);
    if (!through) return null;
    r = dist(center.pos, through.pos);
  } else {
    const a = P(d.radiusA);
    const b = P(d.radiusB);
    if (!a || !b) return null;
    r = dist(a.pos, b.pos);
  }
  if (!(r > 1e-12)) return null;
  return { id, kind: 'circle', def: d, name, c: center.pos, r };
}

function buildPoint(
  def: PointDef,
  id: GeoId,
  P: (g: GeoId) => GPoint | null,
  anyObj: (g: GeoId) => GeoObject | null,
  givens: Readonly<Record<string, Vec2>>,
): Vec2 | null {
  switch (def.via) {
    case 'given': {
      return givens[id as string] ?? null;
    }
    case 'free':
      return def.at;
    case 'lattice':
      return { x: def.i * def.step, y: def.j * def.step };
    case 'midpoint': {
      const a = P(def.a);
      const b = P(def.b);
      if (!a || !b) return null;
      if (dist(a.pos, b.pos) < 1e-12) return null;
      return midpoint(a.pos, b.pos);
    }
    case 'on-object': {
      const host = anyObj(def.host);
      if (!host) return null;
      if (host.kind === 'line') {
        // t medido a partir de `p`, em unidades do comprimento |q - p|
        const L = Math.max(dist(host.p, host.q), 1e-9);
        return add(host.p, scale(host.dir, def.t * L));
      }
      if (host.kind === 'circle') {
        return add(host.c, fromAngle(def.t, host.r));
      }
      return null;
    }
    case 'reflect': {
      const p = P(def.p);
      const mirror = anyObj(def.mirror);
      if (!p || !mirror || mirror.kind !== 'line') return null;
      const foot = projectOnLine(p.pos, { p: mirror.p, dir: mirror.dir });
      return add(foot, sub(foot, p.pos));
    }
    case 'intersection': {
      const oa = anyObj(def.a);
      const ob = anyObj(def.b);
      if (!oa || !ob) return null;
      const pts = intersectionPoints(oa, ob);
      return pts[def.branch] ?? pts[0] ?? null;
    }
  }
}

/**
 * Pontos de interseccao entre dois objetos, em ORDEM DETERMINISTICA.
 *
 * A ordem e o que faz `branch: 0 | 1` significar sempre a mesma coisa entre
 * avaliacoes -- inclusive depois de os dados serem perturbados. Se a ordem variasse,
 * a construcao do aluno "pularia" de ramo durante a checagem de robustez e um
 * trabalho correto seria reprovado.
 */
export function intersectionPoints(a: GeoObject, b: GeoObject): Vec2[] {
  if (a.kind === 'line' && b.kind === 'line') {
    const p = intersectLines(asLineGeom(a), asLineGeom(b));
    return p ? [p] : [];
  }
  if (a.kind === 'line' && b.kind === 'circle') {
    return intersectLineCircle(asLineGeom(a), asCircleGeom(b));
  }
  if (a.kind === 'circle' && b.kind === 'line') {
    return intersectLineCircle(asLineGeom(b), asCircleGeom(a));
  }
  if (a.kind === 'circle' && b.kind === 'circle') {
    return intersectCircles(asCircleGeom(a), asCircleGeom(b));
  }
  return [];
}

function buildLine(
  def: LineDef,
  P: (g: GeoId) => GPoint | null,
  L: (g: GeoId) => GLine | null,
): { p: Vec2; dir: Vec2; span: Span; q: Vec2 } | null {
  switch (def.via) {
    case 'through': {
      const a = P(def.a);
      const b = P(def.b);
      if (!a || !b) return null;
      if (dist(a.pos, b.pos) < 1e-12) return null;
      return { p: a.pos, dir: normalize(sub(b.pos, a.pos)), span: def.span, q: b.pos };
    }
    case 'perpendicular': {
      const p = P(def.through);
      const l = L(def.to);
      if (!p || !l) return null;
      const dir = perp(l.dir);
      return { p: p.pos, dir, span: 'line', q: add(p.pos, dir) };
    }
    case 'parallel': {
      const p = P(def.through);
      const l = L(def.to);
      if (!p || !l) return null;
      return { p: p.pos, dir: l.dir, span: 'line', q: add(p.pos, l.dir) };
    }
    case 'perp-bisector': {
      const a = P(def.a);
      const b = P(def.b);
      if (!a || !b) return null;
      if (dist(a.pos, b.pos) < 1e-12) return null;
      const m = midpoint(a.pos, b.pos);
      const dir = perp(normalize(sub(b.pos, a.pos)));
      return { p: m, dir, span: 'line', q: add(m, dir) };
    }
    case 'bisector': {
      const a = P(def.a);
      const vtx = P(def.vertex);
      const b = P(def.b);
      if (!a || !vtx || !b) return null;
      const u = normalize(sub(a.pos, vtx.pos));
      const w = normalize(sub(b.pos, vtx.pos));
      const s = add(u, w);
      // u e w opostos: o angulo e raso e a bissetriz e indefinida.
      if (Math.hypot(s.x, s.y) < 1e-9) return null;
      const dir = normalize(s);
      return { p: vtx.pos, dir, span: 'ray', q: add(vtx.pos, dir) };
    }
  }
}

/**
 * Um objeto e derivado dos dados, ou foi posto a olho?
 *
 * Um objeto e "ancorado" se, seguindo suas dependencias ate as folhas, nenhuma folha
 * for `free`. E este teste que distingue construir de estimar -- e ele existe porque
 * o snapping registra proveniencia honesta em vez de so mover um pixel.
 */
export function isAnchored(snap: ConstructionSnapshot, id: GeoId): boolean {
  const seen = new Set<string>();
  const stack: GeoId[] = [id];
  while (stack.length) {
    const cur = stack.pop() as GeoId;
    const key = cur as string;
    if (seen.has(key)) continue;
    seen.add(key);
    const def = snap.defs[key];
    if (!def) return false;
    if (def.via === 'free') return false;
    stack.push(...dependenciesOf(def));
  }
  return true;
}

export { lineThrough, angleOf, lerp };
